import { Build, Component, Element, Method, Prop, State, VNode, h } from '@stencil/core';
import { Width, Target } from '../types';
import { ButtonAppearance, ButtonPalette } from './types';
import { closestElementCrossShadowBoundary, mapGet, queryElementRoots } from '../../utils';

/**
 * Passing a 'href' will render an anchor link, instead of a button. Role will
 * be set to link, or button, depending on this. It is the consumers
 * responsibility to add aria information, rel, target, for links, and any
 * button attributes for form submission.
 *
 * @slot - Adds button text.
 */
@Component({
  shadow: true,
  styleUrl: 'button.css',
  tag: 'abds-button',
})
export class Button {
  //--------------------------------------------------------------------------
  //
  //  Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsButtonElement;

  //--------------------------------------------------------------------------
  //
  //  Props
  //
  //--------------------------------------------------------------------------

  /**
   * Provides an outlined appearance (i.e. solid border) to the button.
   * Currently, only brand and destructive variants support outline.
   */
  @Prop({ reflect: true }) appearance: ButtonAppearance = 'solid';

  // TODO: Update type to ButtonType. buttonType should be changed to type.
  /** Sets button type attribute; defaults to **button** if not specified. */
  @Prop({ mutable: true }) buttonType: string;

  /** Disables button interaction. */
  @Prop({ reflect: true }) disabled = false;

  /** Form ID associated with component. */
  @Prop() formId: string;

  /** Sets anchor href attribute; determines if component should render as anchor or button. */
  @Prop({ reflect: true }) href: string;

  /** Adds an abds-icon to end of button text. */
  @Prop({ reflect: true }) iconEnd: string;

  /** Adds an abds-icon to beginning of button text. */
  @Prop({ reflect: true }) iconStart: string;

  /** Sets aria-label attribute on anchor or button. */
  @Prop() label: string;

  /** Sets button name attribute. */
  @Prop() name: string;

  /** Sets button color. */
  @Prop({ reflect: true }) palette: ButtonPalette = 'brand';

  /** Sets anchor rel attribute. */
  @Prop() rel: string;

  /** Sets anchor target attribute. */
  @Prop() target: Target = '_self';

  /** Sets button width. */
  @Prop({ reflect: true }) width: Width = 'auto';

  //--------------------------------------------------------------------------
  //
  //  Private Properties
  //
  //--------------------------------------------------------------------------

  /** rendered child element */
  private childEl: HTMLAnchorElement | HTMLButtonElement;

  /** rendered child element node type */
  private childElType: 'a' | 'button' = 'button';

  /** watches for changing text content **/
  private observer: MutationObserver;

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  // Act on requested or nearby form based on type.
  private handleClick(event: Event): void {
    const { buttonType } = this;
    const { tagName } = event.currentTarget as HTMLElement;

    // Set focus on click to workaround Safari issue on OX X 10.X.
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
    this.setFocus();

    // this.buttonType refers to type attribute, not child element type
    if (tagName === 'BUTTON' && buttonType !== 'button') {
      event.preventDefault();

      const { formId, host } = this;
      const targetForm: HTMLFormElement = formId
        ? queryElementRoots(host, `#${formId}`)
        : closestElementCrossShadowBoundary(host, 'form');

      if (targetForm) {
        const targetFormSubmitFunction = targetForm.onsubmit as () => void;
        const eventTypeHandlers = {
          reset: (): void => {
            targetForm.reset();
          },
          submit: (): void => {
            if (targetFormSubmitFunction) targetFormSubmitFunction();
            else if (targetForm.checkValidity()) targetForm.submit();
            else targetForm.reportValidity();
          },
        };

        const eventTypeHandler = mapGet(buttonType, eventTypeHandlers);
        typeof eventTypeHandler === 'function' && eventTypeHandler();
      }
    }
  }

  private updateHasContent = () => {
    const slottedContent = this.host.textContent.trim().length > 0 || this.host.childNodes.length > 0;

    this.hasContent =
      this.host.childNodes.length === 1 && this.host.childNodes[0]?.nodeName === '#text'
        ? this.host.textContent?.trim().length > 0
        : slottedContent;
  };

  //--------------------------------------------------------------------------
  //
  //  Private State
  //
  //--------------------------------------------------------------------------

  /** Determine if slotted content is present for styling purposes. */
  @State() private hasContent = false;

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  /** Sets focus on child element. */
  @Method()
  async setFocus(): Promise<void> {
    const { childEl } = this;

    childEl.focus();
  }

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  componentWillLoad(): void {
    if (Build.isBrowser) {
      const { buttonType, childElType, updateHasContent } = this;

      updateHasContent();

      if (childElType === 'button' && buttonType !== '') this.buttonType = 'submit';
    }
  }

  componentWillRender(): void {
    const { href } = this;
    this.childElType = href ? 'a' : 'button';
  }

  connectedCallback(): void {
    const setupTextContentObserver = () => {
      if (Build.isBrowser) {
        this.observer = new MutationObserver(() => {
          this.updateHasContent();
        });

        this.observer.observe(this.host, { childList: true, subtree: true });
      }
    };

    this.handleClick = this.handleClick.bind(this);
    setupTextContentObserver();
  }

  disconnectedCallback(): void {
    const { observer } = this;

    observer.disconnect();
  }

  render(): VNode {
    const {
      buttonType,
      childElType: Tag,
      disabled,
      hasContent,
      href,
      iconEnd,
      iconStart,
      label,
      name,
      handleClick,
      rel,
      target,
    } = this;

    return (
      <Tag
        aria-label={label}
        disabled={disabled}
        href={href}
        name={Tag === 'button' && name}
        onClick={!disabled && handleClick}
        ref={(element: HTMLAnchorElement | HTMLButtonElement) => {
          if (element) this.childEl = element;
        }}
        rel={Tag === 'a' && rel}
        tabIndex={disabled ? -1 : null}
        target={Tag === 'a' && target}
        type={Tag === 'button' && (buttonType || 'button')}
      >
        {iconStart && <abds-icon icon={iconStart} size="md" stroke-width="bold" />}
        {hasContent && <slot />}
        {iconEnd && <abds-icon icon={iconEnd} size="md" stroke-width="bold" />}
      </Tag>
    );
  }
}
